// SEO utility functions for dynamic meta tags and Schema.org JSON-LD

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
}

export const updateMetaTags = (config: SEOConfig) => {
  // Update title
  document.title = config.title;

  // Update or create meta tags
  const metaTags = [
    { name: 'description', content: config.description },
    { name: 'keywords', content: config.keywords || '' },
    { property: 'og:title', content: config.title },
    { property: 'og:description', content: config.description },
    { property: 'og:url', content: config.canonical || window.location.href },
    { property: 'og:image', content: config.ogImage || '' },
    { name: 'twitter:title', content: config.title },
    { name: 'twitter:description', content: config.description },
    { name: 'twitter:image', content: config.ogImage || '' },
    { name: 'robots', content: config.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' }
  ];

  metaTags.forEach(({ name, property, content }) => {
    if (!content) return;

    const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
    let element = document.querySelector(selector);

    if (!element) {
      element = document.createElement('meta');
      if (name) element.setAttribute('name', name);
      if (property) element.setAttribute('property', property);
      document.head.appendChild(element);
    }

    element.setAttribute('content', content);
  });

  // Update canonical link
  if (config.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = config.canonical;
  }

  // Update last-modified
  const lastModified = document.querySelector('meta[name="last-modified"]');
  if (lastModified) {
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
  }
};

export const addSchemaOrg = (schema: object) => {
  // Remove existing schema if present
  const existingSchema = document.querySelector('script[type="application/ld+json"]');
  if (existingSchema) {
    existingSchema.remove();
  }

  // Add new schema
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
};

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    name: 'Commercial Truck & Van Center',
    description: 'Quality used commercial trucks and cargo vans dealer in Montreal, Quebec',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    image: `${siteUrl}/og-image.jpg`,
    telephone: '+1-514-571-7256',
    email: 'camionjma@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Montreal',
      addressLocality: 'Montreal',
      addressRegion: 'QC',
      postalCode: '',
      addressCountry: 'CA'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 45.5017,
      longitude: -73.5673
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 45.5017,
        longitude: -73.5673
      },
      geoRadius: '50000'
    },
    priceRange: '$$',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '17:00'
      }
    ],
    sameAs: [
      'https://www.facebook.com/yourpage',
      'https://www.instagram.com/yourpage'
    ]
  };
};

export const generateProductSchema = (vehicle: any) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: vehicle.title,
    description: vehicle.description || `${vehicle.title} - Quality commercial vehicle`,
    image: vehicle.image_url || vehicle.image,
    brand: {
      '@type': 'Brand',
      name: vehicle.title.split(' ')[0]
    },
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}/#vehicle-${vehicle.id}`,
      priceCurrency: 'CAD',
      price: vehicle.price,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/UsedCondition',
      seller: {
        '@type': 'AutoDealer',
        name: 'Commercial Truck & Van Center'
      }
    },
    vehicleConfiguration: vehicle.features?.join(', ') || '',
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: vehicle.mileage || 0,
      unitCode: 'KMT'
    },
    productionDate: vehicle.year?.toString() || ''
  };
};

export const generateWebPageSchema = (pageType: string, title: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: `${siteUrl}${window.location.pathname}`,
    inLanguage: 'en-CA',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Commercial Truck & Van Center',
      url: siteUrl
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: title,
          item: `${siteUrl}${window.location.pathname}`
        }
      ]
    }
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const generateItemListSchema = (vehicles: any[]) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: vehicles.map((vehicle, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: vehicle.title,
        image: vehicle.image_url || vehicle.image,
        offers: {
          '@type': 'Offer',
          price: vehicle.price,
          priceCurrency: 'CAD',
          availability: 'https://schema.org/InStock'
        }
      }
    }))
  };
};
